import { useLanguage } from '@/contexts/LanguageContext';
import { motion } from 'framer-motion';
import { Zap } from 'lucide-react';
import {
  useCalculator, PopularChips, VehicleCards, TrustBadges,
  PriceResult, AZFilter, DestinationDropdown, CTAButtons,
} from './CalculatorShared';

interface Props { onInteraction: () => void; onConversion: () => void; }

const VariantB = ({ onInteraction, onConversion }: Props) => {
  const { t } = useLanguage();
  const calc = useCalculator(onInteraction);

  return (
    <section id="calculator" className="py-20 md:py-28 bg-background" data-ab-variant="B">
      <div className="container">
        <div className="grid lg:grid-cols-5 gap-8 items-start">
          {/* Left content */}
          <div className="lg:col-span-3 space-y-6">
            <div>
              <h2 className="font-display text-3xl md:text-4xl font-bold text-foreground mb-4">{t.calculator.title}</h2>
              <p className="text-muted-foreground text-lg">{t.calculator.subtitle}</p>
              <div className="w-16 h-1 bg-gradient-gold mt-6 rounded-full" />
            </div>

            <div>
              <p className="text-xs font-semibold text-muted-foreground mb-2 uppercase tracking-wide">{t.calculator.popularDestinations}</p>
              <PopularChips onChipClick={calc.handleChipClick} />
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground mb-2">{t.calculator.azFilter}</label>
              <AZFilter activeLetter={calc.activeLetter} availableLetters={calc.availableLetters} onLetterClick={calc.handleLetterClick} />
            </div>

            <TrustBadges className="justify-start" />
          </div>

          {/* Right side card */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            className="lg:col-span-2 bg-card rounded-2xl shadow-card border border-border p-5 space-y-4 lg:sticky lg:top-24"
            data-ab-variant="B"
          >
            <div className="flex items-center gap-2 mb-2">
              <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-primary/10 text-primary text-xs font-bold">
                <Zap className="w-3.5 h-3.5" /> Sofort Preis
              </span>
            </div>

            <DestinationDropdown
              isOpen={calc.isOpen} selected={calc.selected} grouped={calc.grouped}
              dropdownRef={calc.dropdownRef as React.RefObject<HTMLDivElement>}
              inputRef={calc.inputRef as React.RefObject<HTMLInputElement>}
              query={calc.query} setQuery={calc.setQuery} setSelected={calc.setSelected as any}
              setIsOpen={calc.setIsOpen} setActiveLetter={calc.setActiveLetter}
              handleSelect={calc.handleSelect} handleClear={calc.handleClear}
              onInteraction={onInteraction}
            />

            <div>
              <label className="block text-sm font-medium text-foreground mb-2">{t.calculator.vehicleType}</label>
              <VehicleCards vehicleType={calc.vehicleType} setVehicleType={calc.setVehicleType} compact />
            </div>

            {calc.selected && calc.selected.km > 0 && (
              <PriceResult
                selected={calc.selected}
                finalPrice={calc.finalPrice}
                basePrice={calc.basePrice}
                multiplier={calc.multiplier}
                km={calc.selected.km}
              />
            )}

            <div onClick={onConversion}>
              <CTAButtons />
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default VariantB;
